'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import styles from '@/styles/combo.module.scss';
import Navbar from '@/components/navbar';
import { useTranslations } from 'next-intl';
import moment from 'moment-timezone';

import useStore from '@store/index';
import PreloaderComponent from '../preloader';
import { Button, Carousel, Form } from 'react-bootstrap';
import { Minus, MoreVertical, Plus, Settings, X } from 'react-feather';
import CustomizeSelector from '../modals/customizeSelector';
import { ItemObj, OrderDetails, PaidPayObj } from '../modals/order-details-sheet';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { encryptData, formatCurrency, modifiersFilter, removeSpacesFromString } from '_services/common.service';
import { OrderServeDetails } from '@store/slices/orderActions';
import { alertService } from '_services/alert.service';
import { loadingService } from '_services/loading.service';
import { CommonResponse, submitCollection, submitSets } from '_services/submit.service';
import { CartDetails } from './cart';
import CartSuccessSheet from '../modals/cart-success-sheet';
import { useRouter } from 'next/navigation';
import cartsuccessstyles from '@/components/modals/cart-success-sheet.module.scss';
import itemAdded from '@/assets/lotties/item_added.json';
import Link from 'next/link';
import Lottie from 'lottie-react';
import { RemoveScroll } from 'react-remove-scroll';
import { Modifier, ModifierOption, Variation } from './itemview';
import { BiCircle } from 'react-icons/bi';
import { IoMdResize } from 'react-icons/io';
import CustomTooltip from '../common/customtooltip';
import RichTextViewer from '../common/richtextviewer';
import { event } from '@lib/ga';


export interface ComboImageObj {
  id: number,
  image_name: string,
  image_url: string,
  is_primary: boolean,
  status: string
}

export interface ComboOptionObj{
  optionId: number,
  optionDescription: string,
  optionOrder: number,
  items: ItemObj[]
  requirdSelection: number
}

export interface ComboObject {
  dealId: number,
  dealName: string,
  description: string,
  dealCategory: string,
  images: ComboImageObj[],
  options: ComboOptionObj[],
  quantity: number,
  salePrice: {
    amount: number,
    currency: string
  }
  amount: {
    amount: number,
    currency: string
  },
  amountType: string,
  applyMode: string,
  isSoldOut: boolean,
  isUpdate: boolean,
  isDelete: boolean
}

interface BottomSheetProps {
  show: boolean;
  onHide: () => void;
  onReset: () => void;
  title: string;
  children: React.ReactNode;
}

const BottomSheet = ({ show, onReset, title, children }: BottomSheetProps) => {
  const t = useTranslations();

  return (<>
    {show && <div className={show ? styles.bottomSheetBackdrop + ' ' + styles.showBottomSheetBackdrop : styles.bottomSheetBackdrop}></div>}

    <div className={`${styles.bottomSheet} ${show ? styles.show : ''}`}>
      <div className={styles.sheetHeader}>
        <h2 className={styles.sheetTitle}>{title.substring(0,55) + (title?.length > 55? '...' : '')}</h2>
        <button className={styles.resetButton} id={`combo-bottom-sheet-reset-button`} onClick={onReset}>{t("ItemView.RESET")}</button>
      </div>
      {children}
  </div>
  </>)};

export default function ComboComponent({
  comboItemObj,
  isBottomSheet,
  toggleBottomSheet
}: {
  comboItemObj: ComboObject | null,
  isBottomSheet: boolean,
  toggleBottomSheet: unknown
}) {
  const t = useTranslations();
  const router = useRouter();

  const [showPizzaSheet, setShowPizzaSheet] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [showSuccessSheet, setShowSuccessSheet] = useState(false);

  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number | null>(null);
  const [selectedOption, setSelectedOption] = useState<ComboOptionObj | null>(null);
  const [selectedOptionItem, setSelectedOptionItem] = useState<ItemObj | null>(null);
  const [notes, setNotes] = useState<string>('');

  const { hasHydrated, identityToken, cart, addToCart } = useStore((state) => state);

  const [selectedComboItem, setSelectedComboItem] = useState<ComboObject | null>(null);

  const [ isComboCompleted, setIsComboCompleted ] = useState<boolean>(false);
  const [ subtotalPrice, setSubtotalPrice ] = useState<number>(0);
  const [ comboDiscount, setComboDiscount ] = useState<number>(0);
  // const [ fixedModifierTotal, setFixedModifierTotal ] = useState<number>(0);
  const [ totalPrice, setTotalPrice ] = useState<number>(0);
  const [showDescriptionIndex, setShowDescriptionIndex] = useState<number | null>(null);
  const [initialComboItem, setInitialComboItem] = useState<ComboObject | null>(null);

  const defaultOrderDetails = () => ({
    orderId: "",
    orderVersion: 0,
    locationId: "",
    customerId: identityToken || "",
    lineItems: [] as ItemObj[],
    fulfillments: [] as OrderServeDetails[],
    payments: [] as PaidPayObj[],
    isDealItem: true,
    dealId: selectedComboItem?.dealId || 0,
    isUpdate: false,
    isDelete: false,
    } as OrderDetails);

    useEffect(() => {
      // console.log(comboItemObj);
      
      if (comboItemObj) {
        const updateComboItemObj = structuredClone(comboItemObj);

        updateComboItemObj?.options?.forEach((option, index) => {
          option.items?.forEach((item, itemIndex) => {
            item.itemUniqueId = `${item.itemId}-${index}${itemIndex}`;

            item.modifiers?.forEach(modifier => {
              modifier.mofireOptions?.forEach(mofireOption => {
                  mofireOption.isApplied = (mofireOption?.isDefaultSelected || false);
              });
            });
          });
        });

        // Sort options based on optionOrder
        updateComboItemObj.options.sort((a, b) => a.optionOrder - b.optionOrder);
        
        // Sort option items based on sold out status (sold out items at the end)
        // Also check applied variation sold out status
        updateComboItemObj.options.forEach(option => {
          option.items.sort((a, b) => {
            const aVariationSoldOut = a.variations.some(variation => variation.isApplied && variation.isSoldOut);
            const bVariationSoldOut = b.variations.some(variation => variation.isApplied && variation.isSoldOut);
            if (aVariationSoldOut && !bVariationSoldOut) return 1;
            if (!aVariationSoldOut && bVariationSoldOut) return -1;
            if (a.isSoldOut === b.isSoldOut) return 0;
            return a.isSoldOut ? 1 : -1;
          });
        });

        // console.log(structuredClone(updateComboItemObj));
        if (!initialComboItem) {
          // console.log('set initial ComboItem',initialComboItem);
          setInitialComboItem(structuredClone(updateComboItemObj));
        }
        // console.log('test initial ComboItem', updateComboItemObj);
        setSelectedComboItem(updateComboItemObj);
        // setSelectedComboItem(initialComboItem ? initialComboItem :updateComboItemObj);
      }
    }, [hasHydrated]);

    // console.log('view updated ComboItem',selectedComboItem);
    // console.log('view initial ComboItem',initialComboItem);

  // // Preload all option images
  // useEffect(() => {
  //   if (selectedComboItem?.options) {
  //     const preloadImages = async () => {
  //       const promises = selectedComboItem.options.flatMap(option =>
  //         option.items.map(item => {
  //           if (item?.imageUrls?.[0]?.imageUrl) {
  //             return new Promise<void>((resolve) => {
  //               const img = document.createElement('img');
  //               img.onload = () => resolve();
  //               img.onerror = () => resolve();
  //               img.src = item.imageUrls[0].imageUrl;
  //             });
  //           }
  //           return Promise.resolve();
  //         })
  //       );
  //       await Promise.all(promises);
  //     };

  //     void preloadImages();
  //   }
  // }, [selectedComboItem?.options]);

  useEffect(() => {
    if (selectedComboItem) {
      let modifierPrice = 0;

      const price: number = selectedComboItem.options.reduce((acc, option) => {
        const selectedItem = option.items.filter(item => item.isApplied);

        let itemPrice = 0;

        // if (selectedItem && selectedItem.length > 0) {
        //   for(let i=0; i<selectedItem.length; i++){
        //     const itemModCharges = (selectedItem[i]?.modifiers?.reduce((modAcc, mod) => {
        //       const selectedModifier = mod.mofireOptions.find(opt => opt.isApplied);
        //       const price = (selectedModifier?.optionPrice?.amount || 0) * (parseFloat(selectedModifier?.quantity.toString() || '1') > 0 ? parseFloat(selectedModifier?.quantity.toString() || '1') : 1);
        //       // Only add positive prices, ignore negative or zero
        //       return modAcc + (price > 0 ? price : 0);
        //     }, 0) || 0);
        //     modifierPrice += (selectedItem[i]?.price?.amount || 0) + itemModCharges;
        //   }
        // }
        
        // return acc + modifierPrice;

        if (selectedItem && selectedItem.length > 0) {
          for(let i=0; i<selectedItem.length; i++){
            const itemModCharges = (selectedItem[i]?.modifiers?.reduce((modAcc, mod) => {
              const appliedModifiers = mod.mofireOptions.filter(opt => opt.isApplied);
              
              const modifierTotal = appliedModifiers.reduce((sum, selectedModifier) => {
                const price = (selectedModifier?.optionPrice?.amount || 0) * 
                              (parseFloat(selectedModifier?.quantity?.toString() || '1') > 0 
                                ? parseFloat(selectedModifier?.quantity?.toString() || '1') 
                                : 1);
                
                // console.log('selectedModifier', selectedModifier);
                // console.log('selectedModifierPrice', price);
                
                return sum + (price > 0 ? price : 0);
              }, 0);
              
              return modAcc + modifierTotal;
            }, 0) || 0);
            
            // console.log('itemModCharges', itemModCharges);
            // modifierPrice += (selectedItem[i]?.price?.amount || 0) + itemModCharges;
            modifierPrice += itemModCharges;

            itemPrice += (selectedItem[i]?.variations.filter(variation => variation.isApplied).map(variation => variation?.variationPrice?.amount || 0).reduce((a, b) => a + b, 0) || 0);

          }
        }

        return acc + itemPrice;
      }, 0);

      const quantity = (parseFloat(selectedComboItem.quantity.toString()) || 1);
      // const finalPrice = (price * quantity);
      let finalPrice = price;

      /* if(comboItemObj?.applyMode === "discount"){
        finalPrice += (modifierPrice * quantity);
      } */

      setIsComboCompleted(selectedComboItem.options.every(option => option.items.filter(item => item.isApplied).length === option.requirdSelection));
      
      // Combo discount logic
      let discount = 0;
      if(comboItemObj?.applyMode === "discount"){
        if(comboItemObj?.amountType === "amount"){
          discount = (finalPrice > 0 ? (Math.round(comboItemObj?.amount?.amount || 0) * quantity) : 0);
          finalPrice = finalPrice * quantity;
        } else{
          discount = (finalPrice > 0 ? (Math.round((finalPrice / 100) * (comboItemObj?.amount?.amount || 0)) * quantity) : 0);
          finalPrice = finalPrice * quantity;
        }
      } else{
        finalPrice = finalPrice * quantity;
        discount = (finalPrice > 0 ? (finalPrice - (Math.round(selectedComboItem?.amount?.amount) * quantity || 0)) : 0);
      }
      // finalPrice = finalPrice * quantity;

      // setFixedModifierTotal((modifierPrice * quantity));
      setComboDiscount((discount));
    
      const finalSubtotal = finalPrice + (modifierPrice * quantity);
      setSubtotalPrice(finalSubtotal > 0 ? finalSubtotal : 0);

      const fixModTotal = (finalSubtotal - (discount));
      setTotalPrice(fixModTotal > 0 ? fixModTotal : 0);

      // if(comboItemObj?.applyMode === "discount"){
      //   setComboDiscount(discount);

      //   const finalSubtotal = finalPrice + (modifierPrice * quantity);
      //   setSubtotalPrice(finalSubtotal > 0 ? finalSubtotal : 0);
        
      //   // setFixedModifierTotal(0);
      //   setTotalPrice((finalPrice - discount) > 0 ? (finalPrice - discount) : 0);
      // } else{
      //   // setFixedModifierTotal((modifierPrice * quantity));
      //   setComboDiscount(discount);

      //   const finalSubtotal = finalPrice + (modifierPrice * quantity);
      //   setSubtotalPrice(finalSubtotal > 0 ? finalSubtotal : 0);

      //   const fixModTotal = (finalSubtotal - discount);
      //   setTotalPrice(fixModTotal > 0 ? fixModTotal : 0);
      // }

    }
  }, [selectedComboItem]);

  if (!hasHydrated) {
    return <PreloaderComponent />; // Prevent rendering until store is hydrated
  }


  const handleSetSelectOption = (index: number,itemUniqueId:string,isShowCustomize: boolean = false) => {
    const option = selectedComboItem?.options[index] || null;
    // const option = selectedComboItem?.options?.find(opt =>opt.items?.some(item => item.itemId === index)) || null;
    setSelectedOption(option);
    setSelectedOptionIndex(index);
    // console.log(index);
    // console.log("selectedComboItem",selectedComboItem);

    if(isShowCustomize){
      const selectedOptionItem =
        selectedComboItem?.options
          ?.flatMap(option => option.items)
          ?.find(item => item.itemUniqueId === itemUniqueId) || null;

      // const selectedOptionItem = option?.items.find(item => item.itemId === index.toString()) || null;
      setSelectedOptionItem(selectedOptionItem);
      // console.log('selectedOptionItem',selectedOptionItem);
      // console.log('option',option);
      setShowCustomize(true);
    } else{
      setShowPizzaSheet(true);
    }
  };

  // console.log(selectedComboItem?.options);

  const handleSetOptionItem = (index: number, isApplied: boolean) => {
    const option = selectedComboItem?.options[selectedOptionIndex || 0];
    
    if (option) {
      const item = option.items[index];
      if (item) {
        // If single selection, unselect all other items
        if(option.requirdSelection === 1){
          option.items.forEach(itm => {
            itm.isApplied = false;
          });
        }

        // if required selection count reached, unselect the first selected item
        // if(option.requirdSelection > 1 && option.items.filter(itm => itm.isApplied).length >= option.requirdSelection && !item.isApplied){
        //   const firstSelected = option.items.find(itm => itm.isApplied);
        //   if(firstSelected){
        //     firstSelected.isApplied = false;
        //     alertService.error(`You can only select ${option.requirdSelection} choices. The first selected choice was replaced.`);
        //   }
        // }

        // if required selection count reached, desable another items and show error msg
        if(option.requirdSelection > 1 && option.items.filter(itm => itm.isApplied).length >= option.requirdSelection && !item.isApplied){
          alertService.error(t('Combo.SELECTION_RESTRICT_NOTICE', { max: option.requirdSelection }), 3000, true);
        }
        else{
          item.isApplied = (option.requirdSelection === 1) ? !item.isApplied : isApplied;
        }

        // item.isApplied = (option.requirdSelection === 1) ? !item.isApplied : isApplied;

        // console.log(selectedComboItem);

        setSelectedComboItem({ ...selectedComboItem });
      }
    }
  };

  // Handle changes to modifier options
  const handleModifierChange = (optionId: string, index: number, isQuantityChange: boolean, applyValue: unknown) => {
    // console.log(optionId, index, isQuantityChange, applyValue, isSideUpdate);

    const option = selectedOptionItem?.modifiers?.find(mod => mod.modifierId.toString() === optionId);
    if (option) {
      const item = option.mofireOptions[index];
      if (item) {
        if (isQuantityChange) {
          item.quantity = (parseInt((item.quantity).toString()) + (applyValue as number));
          item.isApplied = (item.quantity > 0);
          
        } else {
          if(option.selectionType === "MULTIPLE"){
            item.isApplied = (applyValue as boolean);
          } else{
            option.mofireOptions.forEach(opt => {
              if (opt.optionId !== item.optionId){
                opt.isApplied = false;
              }
            });

            item.isApplied = true;
          }

        }

        // console.log(option);
        
        setSelectedOptionItem({ ...selectedOptionItem! });

        const updatedComboItem = { ...selectedComboItem! };
        updatedComboItem.options.forEach(opt => {
          opt.items.forEach(item => {
            if (item.itemUniqueId === selectedOptionItem?.itemUniqueId) {
              item = { ...selectedOptionItem! };
            }
          });
        });

        setSelectedComboItem(updatedComboItem);
      }
    }
  };
  
  /* const handleResetAllSelectedOption = () => {
    if (initialComboItem) {
      // console.log('initialComboItem',initialComboItem);
      setSelectedComboItem(structuredClone(initialComboItem));
    }
  }; */

  const handleResetSelectedOption = () => {
    const option = selectedOption;
    if (option) {
      option.items.forEach(item => {
        item.isApplied = false;
      });
      setSelectedOption({ ...option });

      const updatedComboItem = { ...selectedComboItem! };
      updatedComboItem.options.forEach(opt => {
        opt.items.forEach(item => {
          if (item.itemUniqueId === selectedOptionItem?.itemUniqueId) {
            item = { ...selectedOptionItem! };
          }
        });
      });

      setSelectedComboItem(updatedComboItem);
      // setShowPizzaSheet(false);
    }
  };

  const handleSetDealQuantity = (quantity: number) => {
    if (selectedComboItem) {
      setSelectedComboItem({ ...selectedComboItem, quantity });
    }
  };

  const loadCart = async (cartDetails: CartDetails, isNew: boolean = false) => {
      const cartRes = await submitSets(submitCollection.viewCartOrder, {
        orderId: cartDetails?.orderId || ""
      }, true, {});
      
      loadingService.loading(false);
      if (cartRes && cartRes.status && cartRes.extra) {
        addToCart(cartRes.extra);
  
        if (isNew) {
          setShowSuccessSheet(true);
        }
      } else {
        alertService.error(t('Cart.LOAD_FAILED'), 3000, true);
      }
  };

  const handleAddToCart = () => {
    if (selectedComboItem) {
      //validate all selections selected with required selection count
      if(!selectedComboItem.options.every(option => option.items.filter(item => item.isApplied).length === option.requirdSelection)) {
        alertService.error("Please select all required options.");
        return;
      }

      const updatedOrderDetails = structuredClone(defaultOrderDetails());

      if(cart && cart.orderId && cart.orderId !== ""){
        updatedOrderDetails.orderId = (cart?.orderId || "");
        updatedOrderDetails.orderVersion = (cart?.orderVersion || 0);
        updatedOrderDetails.locationId = (cart?.locationId || "");
        updatedOrderDetails.isDelete = false;
        updatedOrderDetails.isUpdate = true;
        updatedOrderDetails.fulfillments = [];
      }

      updatedOrderDetails.customerId = identityToken || "";
      updatedOrderDetails.customerTimezone = moment.tz.guess() || "UTC";

      updatedOrderDetails.lineItems = selectedComboItem.options.reduce((acc: ItemObj[], option) => {
        const selectedItems = option.items.filter(item => item.isApplied);
        if (selectedItems.length > 0) {
          selectedItems.forEach(item => {
            const newItem = { ...item };
            newItem.quantity = selectedComboItem.quantity || 1;
            newItem.isUpdate = false;
            newItem.isDelete = false;
            newItem.modifiers = (item?.modifiers || []).map(mod => ({
              ...mod,
              mofireOptions: (mod?.mofireOptions || []).reduce((modAcc: ModifierOption[], opt) => {
                if (opt.isApplied) {
                  opt.quantity = opt.quantity? (parseInt(opt.quantity.toString()) > 1 ? parseInt(opt.quantity.toString()) : 1) : 1;
                  modAcc.push(opt);
                }
                return modAcc;
              }, [])
            }));
            newItem.variations = (item?.variations || []).filter(variation => variation.isApplied);
            newItem.notes = notes || '';

            acc.push(newItem);
          });
        }
        return acc;
      }, []);

      // Add the selected combo item to the cart
      console.log("Adding to cart:", updatedOrderDetails);

      loadingService.loading(true);
      submitSets(submitCollection.saveOrder, updatedOrderDetails, false, null, false).then((res: CommonResponse) => {
        // console.log('Order Save Response:', res);
        
        if (res && res.status) {
          // handleResetAllSelectedOption();

          if(res.extra){
            
            // let resData = cart;
            // resData.orderId = res.extra.orderId;
            // resData.orderVersion = res.extra.orderVersion;
            // resData.locationId = res.extra.locationId;
            // resData.lineItems = cart.lineItems.concat(updatedOrderDetails.lineItems);
  
            // addToCart(resData);
            loadCart(res.extra as CartDetails, true);
          }
  
          // setTimeout(() => {
          //   router.push('/cart');
          // }, 2000);
        } else {
          loadingService.loading(false);
          alertService.error(t('ItemView.ADD_TO_CART_FAILED'), 3000, true);
        }
      }).catch((error: ErrorOptions) => {
        loadingService.loading(false);
        console.error('Error saving order:', error);
        alertService.error(t('ItemView.ERROR_SAVING_ORDER'), 3000, true);
      });
    }
  };

  const totalPriceFormatted = () => {
    // const quantity = (parseFloat((selectedComboItem?.quantity || 1).toString()) || 1);
    const currency = selectedComboItem?.options[0]?.items[0]?.price?.currency || 'USD';
    return formatCurrency(totalPrice, currency);
  };

  const resetSelectedOptionItem = () => {
    const updatedOptionItem = {
      ...structuredClone(selectedOptionItem!),
      modifiers: (selectedOptionItem?.modifiers || []).map((mod) => ({
        ...mod,
        mofireOptions: (mod.mofireOptions || []).map((opt) => ({
          ...opt,
          isApplied: (opt.isDefaultSelected || false)
          // isApplied: false
        }))
      }))
    };

    setSelectedOptionItem((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        modifiers: updatedOptionItem.modifiers
      } as ItemObj;
    });

    const updatedComboItem = { ...selectedComboItem! };
    updatedComboItem.options.forEach(opt => {
      opt.items.forEach(item => {
        if (item.itemUniqueId === updatedOptionItem?.itemUniqueId) {
          item.modifiers = updatedOptionItem.modifiers;
        }
      });
    });

    setSelectedComboItem({
      ...updatedComboItem
    });

  };

  const cartId = (cart && cart.orderId && cart.orderId !== ""? "?c=" + encryptData(cart.orderId) : "");

  // console.log(selectedComboItem);
  
  return (
  <RemoveScroll enabled={showCustomize || isBottomSheet ||showPizzaSheet || showSuccessSheet}>
    {!isBottomSheet && <Navbar pageTitle={selectedComboItem?.dealName || "Combo"} />}

    <div className={styles.comboPage + (isBottomSheet ? ' ' + styles.withOutNavbar : '')}>
      <div className={styles.comboContent}>
        
        {/* Item view back button */}
        <div style={{ display: isBottomSheet ? 'none' : 'block' }}>
          <Button variant='secondary' id={`item-view-go-back-button`} onClick={() => window.history.back()} className={styles.singleViewBackButton}><X size={20} /></Button>
        </div>

        <div className={styles.promoCard}>
        <div className={styles.promoContent}>
          <div className={styles.imageSection}>
            <div className={styles.imageWrapper}>
              <Carousel
                interval={null}
                className={styles.imageCarousel}
                
              >
                {selectedComboItem?.images && selectedComboItem?.images.length > 0 && selectedComboItem?.images.map((image: ComboImageObj, index: number) => (
                  <Carousel.Item key={index}>
                    <PhotoProvider>
                      <PhotoView src={image.image_url || 'assets/images/placeholder.png'}>
                        <Image
                          id={`combo-preview-image-${index}`}
                          src={image.image_url}
                          alt={image.image_name || 'combo meal'}
                          width={460}
                          height={476}
                          className={styles.itemImage}
                          placeholder='blur'
                          blurDataURL="/assets/images/placeholder.png"
                        />
                      </PhotoView>
                    </PhotoProvider>
                  </Carousel.Item>
                ))}
              </Carousel>

            </div>
            
          </div>
        </div>
      </div>

      {selectedComboItem?.isSoldOut && <div className={styles.soldOutBanner}>
        <p>{t('ItemView.SOLD_OUT_MESSAGE')}</p>
      </div>}

      <div className={styles.selectionArea}>

        {/* Description Section */}
        <div className={styles.contentSection}>
          {selectedComboItem?.description ? <p className={styles.description}>
            {selectedComboItem?.description}
          </p> : <></>}
        </div>

        {/* <button onClick={handleResetAllSelectedOption}>
          reset
        </button> */}

        {selectedComboItem && selectedComboItem.options && selectedComboItem.options.sort((a, b) => a.optionOrder - b.optionOrder).map((option, index) => (
          <div key={index} data-combooption={removeSpacesFromString(option.optionDescription)} className={styles.comboOption}>
            {index === 0 && selectedComboItem?.dealName && (
              <h1 style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}>
                {selectedComboItem.dealName}
              </h1>
            )}
            <h3 className={styles.sectionTitle}>
              {option.optionDescription}
            
              {/* {option.requirdSelection === 1 && option.items && option.items.length > 0 && (option.items.find(item => item.isApplied)?.modifiers || []).length > 0 && (
                <Button 
                  variant='warning' 
                  id={`combo-option-${index}-customize-button`} 
                  className={styles.customizeButton} 
                  onClick={() => handleSetSelectOption(index, true)}
                  data-id="option-customize-button"
                  >
                  <Settings size={16} />
                </Button>
              )} */}
            </h3>
            <div style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
              {option.items && option.items.length > 0 && option.items.filter(item => item.isApplied).length > 0 ? 
                (option.items.filter(item => item.isApplied).map((item, itemIndex) => {
                  return <div key={`combo-option-${index}-item-${itemIndex}-selected-button`} style={{ marginBottom: "10px" }}>
                  <div style={{display: 'flex', flexDirection: 'row', width: '100%', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px',gap: '1rem'}}>
                    {/* <button 
                      id={`combo-option-${index}-item-${itemIndex}-selected-button`} 
                      className={styles.dropdownSelector} 
                      onClick={() => handleSetSelectOption(index,'')}
                      data-id="option-select-button"
                      >
                      {item.itemName}
                    </button> */}
                    {item.itemName.length > 45 ? (
                      <CustomTooltip
                        overlayContent={item.itemName}
                        placement="top"
                        scrollElement={null}
                      >
                        <button 
                          id={`combo-option-${index}-item-${itemIndex}-selected-button`} 
                          className={styles.dropdownSelector} 
                          onClick={() => {
                            handleSetSelectOption(index,'');
                          
                            event({
                              action: 'combo_option_item_name_tooltip_viewed',
                              category: 'engagement',
                              label: `Combo Option Item Name Tooltip Viewed: ${item.itemName}`,
                            });
                          }}
                          data-id="option-select-button"
                          >
                          <span>{item.itemName.substring(0, 45) + "..."}</span>
                        </button>
                      </CustomTooltip>
                    ) : (
                      <button 
                          id={`combo-option-${index}-item-${itemIndex}-selected-button`} 
                          className={styles.dropdownSelector} 
                          onClick={() => {
                            handleSetSelectOption(index,'');
                            event({
                              action: 'combo_option_item_name_tooltip_viewed',
                              category: 'engagement',
                              label: `Combo Option Item Name Tooltip Viewed: ${item.itemName}`,
                            });
                          }}
                          data-id="option-select-button"
                          >
                          <span>{item.itemName}</span>
                        </button>
                    )}


                    {item.modifiers && modifiersFilter((item?.modifiers || []), ['dietary','notdietary']).length > 0 &&<span 
                      id={`combo-option-${index}-item-${itemIndex}-customize-button`} 
                      className={styles.customizeButton} 
                      onClick={(e) => {
                        e.stopPropagation();
                        // handleSetSelectOption(itemIndex, true);
                        handleSetSelectOption(-1,(item?.itemUniqueId || item?.itemId), true);
                        
                        event({
                          action: 'combo_option_customize_button_clicked',
                          category: 'engagement',
                          label: `Combo Option Customize Button Clicked: ${item.itemName}`,
                        });
                      }}
                      data-id="option-customize-button"
                      >
                      {/* <Settings size={16} /> */}
                      <CustomTooltip
                        overlayContent={t("ItemView.CUSTOMIZE")}
                        placement="top"
                        scrollElement={null}
                      >
                        <Button variant='warning' id={`item-view-customize-button`} className={styles.customizeButton + ' ' + styles.top} onClick={() => setShowCustomize(true)}>
                          {/* <Plus size={15} /> */}
                          <span><Settings size={16} /></span>
                        </Button>
                      </CustomTooltip>
                    </span>}
                  </div>

                  {/* Show selected add-ons */}
                  {((item?.modifiers || []).length > 0 && (item?.variations || []).length > 0)?<> 
                    <div className={styles.selectedAddons} style={{ marginBottom: '5px' }}>
                      <div className={styles.addOnsList}>
                        {item?.variations && item?.variations.length > 0 && (item?.variations || []).filter(variation => variation.isApplied).map((variation: Variation, varIndex: number) => {
                          return <span key={varIndex} className={`${styles.addonItem} ${styles.base}`}><IoMdResize size={12} /> <span>{variation.variationName} {t("ItemView.SIZE")}

                            {modifiersFilter((item?.modifiers || []), ['dietary']).map((modifier: Modifier) => {
                              const applied = modifier.mofireOptions.filter((modoption: ModifierOption) => modoption.isApplied && !modoption.isDefaultSelected);
                              // console.log(option);
                              return (
                                <React.Fragment key={modifier.modifierId}>
                                  {applied.length > 0 ? (
                                    <>{applied.map((opt: ModifierOption, index: number) => {
                                      return <span key={index}>{` (${opt.optionName})`}</span>;
                                    })}</>
                                  ) : null}
                                </React.Fragment>
                              );
                            })}
                            </span>
                          
                          </span>;
                        })}

                        {modifiersFilter((item?.modifiers || []), ['base']).map((modifier: Modifier) => {
                          const applied = modifier.mofireOptions.filter((modoption: ModifierOption) => modoption.isApplied);
                          // console.log(option);
                          
                          return (
                            <React.Fragment key={modifier.modifierId}>
                              {applied.length > 0 ? (
                                <>{applied.map((opt: ModifierOption, index: number) => {
                                  return <span key={index} className={`${styles.addonItem} ${styles.base}`}><BiCircle size={16} /> {opt.optionName} {t("ItemView.BASE")}
                                  </span>;
                                })}</>
                              ) : null}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  </> : <></>}

                  {modifiersFilter((item?.modifiers || []), ['add', 'ingredient', 'removal']).filter(modifier => modifier.mofireOptions.some(option => option.isApplied)).length > 0 ?<>
                    <div className={styles.selectedAddons}>
                      <div className={styles.addOnsList}>
                        {modifiersFilter((item?.modifiers || []), ['add']).map((modifier: Modifier) => {
                          const applied = modifier.mofireOptions.filter((option: ModifierOption) => option.isApplied);
                          return (
                            <React.Fragment key={modifier.modifierId}>
                              {applied.length > 0 ? (
                                <>{applied.map((opt: ModifierOption, index: number) => {
                                  return <React.Fragment key={index}>
                                    <CustomTooltip
                                      overlayContent={opt.optionName || ''}
                                      placement="top"
                                      scrollElement={null}
                                    >
                                      <span className={styles.addonItem}>+ {opt.quantity > 1 ? `${opt.quantity} x ` : ''} {opt.optionName.substring(0, 20) + (opt.optionName.length > 20 ? '...' : '')}</span>
                                    </CustomTooltip>
                                  </React.Fragment>;
                                })}</>
                              ) : null}
                            </React.Fragment>
                          );
                        })}

                        {modifiersFilter((item?.modifiers || []), ['ingredient', 'removal']).map((modifier: Modifier) => {
                          const isIngredientModifier = modifier.modifierName.toLowerCase().includes("ingred");
                          const removed = modifier.mofireOptions.filter((option: ModifierOption) => (isIngredientModifier ? !option.isApplied : option.isApplied));

                          return (
                            <React.Fragment key={modifier.modifierId}>
                              {removed.length > 0 ? (
                                <>{removed.map((opt: ModifierOption, index: number) => {
                                  return <React.Fragment key={index}>
                                    <CustomTooltip
                                      overlayContent={opt.optionName || ''}
                                      placement="top"
                                      scrollElement={null}
                                    >
                                      <span className={`${styles.addonItem} ${styles.removable}`}>- {opt.quantity > 1 ? `${opt.quantity} x ` : ''} 
                                        {opt.optionName.substring(0, 20) + (opt.optionName.length > 20 ? '...' : '')}</span>
                                    </CustomTooltip>
                                  </React.Fragment>;
                                })}</>
                              ) : null}
                            </React.Fragment>
                          );
                        })}

                      </div>
                    </div>
                  </> : <></>}

                  </div>;
                })) : 
                <button 
                  id={`combo-option-${index}-item-select-button`} 
                  className={styles.dropdownSelector} 
                  onClick={() => {
                    handleSetSelectOption(index, '');
                  
                    event({
                      action: 'combo_option_select_button_clicked',
                      category: 'engagement',
                      label: `Combo Option Select Button Clicked: ${option.optionDescription}`,
                    });
                  }}
                  data-id="option-select-button"
                  style={{ paddingRight: '15px'}}
                  >
                  {(option.requirdSelection > 1 ? `${t("ItemView.SELECT_UP_TO")} ${option.requirdSelection} ${t("ItemView.ITEMS")}` : t("ItemView.SELECT_AN_ITEM"))} 
                  <Image src="/assets/images/arrow-left.svg" alt="Select" width={16} height={16} style={{ transform: 'rotate(270deg)' }} />
                </button>
              }

              {(option.items || []).filter(item => item.isApplied).length > 0 && ((option.items || []).filter(item => item.isApplied).length < option.requirdSelection) && (
                <button 
                  id={`combo-option-${index}-item-select-button`} 
                  className={styles.dropdownSelector} 
                  onClick={() => {
                    handleSetSelectOption(index,'');
                    
                    event({
                      action: 'combo_option_select_button_clicked',
                      category: 'engagement',
                      label: `Combo Option Select Button Clicked: ${option.optionDescription}`,
                    });
                  }}
                  data-id="option-select-button"
                  style={{ paddingRight: '15px'}}
                  >
                  {t("ItemView.SELECT_UP_TO") + ' ' + option.requirdSelection + ' ' + t("ItemView.ITEMS")}
                  <Image src="/assets/images/arrow-left.svg" alt="Select" width={16} height={16} style={{ transform: 'rotate(270deg)' }} />
                </button>
              )}

            </div>
          </div>
        ))}

        <div className={styles.optionGroup} style={{ marginTop: '16px' }}>
          <h2>{t('ItemView.ADDITIONAL_NOTES')}</h2>
          <textarea 
            id='item-view-additional-notes'
            className={styles.notesInput} 
            placeholder={t('ItemView.ADD_SPECIAL_INSTRUCTIONS')}
            value={notes || ''}
            onChange={(e) => {
              setNotes(e.target.value);
            }}
          />
        </div>

        {!isComboCompleted && <div className={styles.incompleteBanner}>
          <p>{t('ItemView.PLEASE_COMPLETE_YOUR_SELECTIONS')}</p>
        </div>}

        {isComboCompleted && <div className={styles.priceSummary}>
          <small className={styles.priceDetailsLabel}>{t('ItemView.PRICE_DETAILS')}</small>
          <div className={styles.priceContainer}>
            <div className={styles.priceRow}>
              {t('ItemView.SUBTOTAL')}
              <span>{formatCurrency(subtotalPrice, selectedComboItem?.amount?.currency || 'USD')}</span>
            </div>
            <div className={` ${styles.priceRow} ${styles.discountRow}`}>
              <span className={styles.discountLabel}>{t('ItemView.COMBO_DISCOUNT')}
                {comboItemObj?.applyMode === "discount" ? <label className={styles.discountBadge}>
                  {(comboItemObj?.amountType === "amount" ? formatCurrency((comboDiscount), (comboItemObj?.amount?.currency || 'USD')) + ' Off' :
                  (comboItemObj?.amountType === "percentage" ? ((comboItemObj?.amount?.amount || 0) + '% Off') : '') )}
                </label> : null}
              </span>
              <span>-{formatCurrency((comboDiscount), comboItemObj?.amount?.currency || 'USD')}</span>
            </div>

            {/* {fixedModifierTotal > 0 && <div className={styles.priceRow}>
              <span className={styles.fixedModifierLabel}>{t('ItemView.MODIFIER_CHARGES')}</span>
              <span>{formatCurrency(fixedModifierTotal, selectedComboItem?.amount?.currency || 'USD')}</span>
            </div>} */}

            <div className={styles.priceRow + ' ' + styles.totalRow}>
              <span className={styles.totalLabel}>{t('ItemView.TOTAL')}</span><span>{totalPriceFormatted()}</span>
            </div>
          </div>
        </div>}

        {!selectedComboItem?.isSoldOut && comboItemObj && <div className={styles.bottomButtons + (isBottomSheet ? ' ' + styles.withBottomNav : '')}>
            <div className={styles.quantitySelector}>
              <button 
                id={`combo-quantity-decrease-button`}
                className={styles.quantityButton} 
                onClick={() => {
                  handleSetDealQuantity(parseInt(selectedComboItem?.quantity.toString() || "1") - 1)
                
                  event({
                    action: 'combo_quantity_decrease_button_clicked',
                    category: 'engagement',
                    label: `Combo Quantity Decrease Button Clicked: New Quantity ${parseInt(selectedComboItem?.quantity.toString() || "1") - 1}`,
                  });
                }}
                disabled={((selectedComboItem?.quantity || 1) === 1) || !isComboCompleted}
            >
              <Minus size={16} />
            </button>
            <span className={styles.quantity}>{selectedComboItem?.quantity || 1}</span>
            <button 
              id={`combo-quantity-increase-button`}
              className={styles.quantityButton} 
              onClick={() => {
                handleSetDealQuantity(parseInt(selectedComboItem?.quantity.toString() || "1") + 1)
                
                event({
                  action: 'combo_quantity_increase_button_clicked',
                  category: 'engagement',
                  label: `Combo Quantity Increase Button Clicked: New Quantity ${parseInt(selectedComboItem?.quantity.toString() || "1") + 1}`,
                });
              }}
              disabled={!isComboCompleted}
            >
              <Plus size={16} />
            </button>
          </div>

          <button id={`combo-add-to-cart-button`} 
            onClick={() => {
              handleAddToCart();
              
              event({
                action: 'combo_add_to_cart_button_clicked',
                category: 'engagement',
                label: `Combo Add to Cart Button Clicked: Combo Name ${selectedComboItem?.dealName}`,
              });
            }} 
            className={styles.addButton}
            disabled={!isComboCompleted || selectedComboItem?.isSoldOut}>
            <Plus size={16} /> {t("ItemView.ADD_TO_CART")} {isComboCompleted && totalPrice > 0 ? ` ${totalPriceFormatted()}` : ''}
          </button>
        </div>}

      </div>
      </div>

      {/* Pizza Customization Sheet */}
      <BottomSheet 
        show={showPizzaSheet} 
        onHide={() => setShowPizzaSheet(false)}
        onReset={() => handleResetSelectedOption()} 
        title={(selectedOption ? `${selectedOption.optionDescription}` : '')}
      >
        <div className={styles.optionGrid}>

          {selectedOption && selectedOption.items && selectedOption.items.length > 0 && selectedOption.items.map((item, index) => {
            const defaultVariation = item.variations ? item.variations.find(variation => variation.isApplied) : null;
            
            return(
              <div className={`${styles.optionCard} ${(item.isSoldOut || (defaultVariation?.isSoldOut || false)) ? styles.soldOut : '' }`} key={index} onClick={() => handleSetOptionItem(index, !item.isApplied)}>
                <Form.Check
                  id={`combo-option-${index}-${(selectedOption?.requirdSelection || 1) > 1 ? "checkbox" : "radio"}`}
                  type={(selectedOption?.requirdSelection || 1) > 1 ? "checkbox" : "radio"}
                  className={styles.checkmark}
                  checked={item.isApplied || false}
                  onChange={() => {}}
                  disabled={item.isSoldOut || (defaultVariation?.isSoldOut || false)}
                />
                <div className={styles.optionImage}>
                  <Image 
                    key={item?.imageUrls?.[0]?.imageUrl}
                    src={item?.imageUrls?.[0]?.imageUrl || '/assets/images/placeholder.png'}
                    alt={item?.itemName} 
                    width={80} 
                    height={80}
                    priority={true}
                    loading="eager"
                    placeholder='blur'
                    blurDataURL="/assets/images/placeholder.png"/>
                </div>
                {/* <span className={styles.optionTitle}>{item.itemName}</span> */}
                <span className={styles.optionTitle}>
                  {item.itemName?.length > 25 ? (
                    <CustomTooltip
                        placement="auto"
                        overlayContent={item.itemName}
                        scrollElement={null}
                        >
                        {item.itemName.substring(0, 25) + (item.itemName?.length > 25 ? '...' : '')}
                        </CustomTooltip>
                  ) : (
                        item.itemName
                      )}

                      <span className={styles.mealPrepContainer}>
                        <span>
                          <CustomTooltip
                            placement="auto"
                            scrollElement={null}
                            overlayContent={item?.isVegetarian ? t('VEGETARIAN') : t('NON_VEGETARIAN')}
                          >
                            {/* <div className={styles.mealPrep + (item?.isVegetarian ? ` ${styles.veg}` : '')}>
                              <span className={styles.dot}></span>
                            </div> */}
                            <div className={styles.mealPrep  + (item?.isVegetarian === null ? `${styles.hidden}`
                              : `${item?.isVegetarian ? ` ${styles.veg}` : ''}`)}>
                              <span className={styles.dot}></span>
                            </div>
                          </CustomTooltip>
                        </span>
                        {item?.itemDescription.length>0 && 
                          <MoreVertical size={21} color="#273525ff" style={{paddingBottom: '0px'}} onClick={(e) => {
                            e.stopPropagation();
                            setShowDescriptionIndex(showDescriptionIndex === index ? null : index);
                          }}/>
                        }
                      </span>
                      {showDescriptionIndex === index && (
                        <span className={styles.comboItemDescription}>
                          <RichTextViewer html={item?.itemDescription} />
                        </span>
                      )}
                  <span className={styles.priceSizeRow}>
                    {(item.isSoldOut || (defaultVariation?.isSoldOut || false)) && <span className={styles.soldOutLabel}>{t('ItemView.SOLD_OUT')}</span>}
                    {!((item.isSoldOut || (defaultVariation?.isSoldOut || false)) ? styles.soldOut : '') && <span className={styles.optionPrice + ' ' + ((item.isSoldOut || (defaultVariation?.isSoldOut || false)) ? styles.soldOut : '')}>
                      (+{item.variations.filter(variation => variation.isApplied).map(variation => formatCurrency((variation?.variationPrice?.amount || 0), (variation?.variationPrice?.currency || 'USD')))})
                    </span>}
                    <span className={styles.optionVariationName + ' ' + ((item.isSoldOut || (defaultVariation?.isSoldOut || false)) ? styles.soldOut : '')}>
                      {defaultVariation?.variationName}
                    </span>
                  </span>
                </span>
              </div>
            );
        })}

        </div>
        <div className={styles.buttonGroup}>
          {selectedOptionIndex !== null && (
            <>
              <button id={`combo-close-button`} onClick={() => setShowPizzaSheet(false)} className={`${styles.button} ${styles.secondary}`}>{t('ItemView.CLOSE')}</button>
              {selectedOptionIndex > 0 && (
                <button id={`combo-previous-option-button`} onClick={() => handleSetSelectOption(selectedOptionIndex - 1,'')} className={`${styles.button} ${styles.primary}`}>{t('ItemView.PREVIOUS')}</button>
              )}
              {selectedOptionIndex < ((selectedComboItem?.options.length || 0) - 1)? (<>
                <button id={`combo-next-option-button`} onClick={() => handleSetSelectOption(selectedOptionIndex + 1,'')} className={`${styles.button} ${styles.primary}`}>{t('ItemView.NEXT')}</button>
              </>): <>
                {(selectedComboItem?.dealCategory || '').toLowerCase().includes('buy more') ? (<>
                  {/* <button className={styles.cancelButton} onClick={() => {setShowPizzaSheet(false);}}>{t('ItemView.CLOSE')}</button> */}
                  <button 
                    id={`combo-apply-button`} 
                    onClick={() => setShowPizzaSheet(false)} 
                    className={`${styles.button} ${styles.primary}`}
                    disabled={selectedOption?.items.filter(item => item.isApplied).length !== (selectedOption?.requirdSelection || 1)}
                    >
                      Add ({selectedOption?.items.filter(item => item.isApplied).length}/{selectedOption?.requirdSelection || 1})
                    </button></>
                ): <>
                  {/* <button className={styles.cancelButton} onClick={() => {setShowPizzaSheet(false);}}>{t('ItemView.CLOSE')}</button> */}
                  <button id={`combo-close-button`} onClick={() => setShowPizzaSheet(false)} className={`${styles.button} ${styles.primary}`}>Finish</button>
                </>}
              </>}
            </>
          )}
        </div>
      </BottomSheet>
      
      {/* Customize Bottom Sheet */}
      <CustomizeSelector
        isHalfAndHalf={false}
        isBottomSheet={isBottomSheet}
        isShowDietaryOptions={true}
        showCustomize={showCustomize}
        setShowCustomize={setShowCustomize}
        modifiers={selectedOptionItem?.modifiers || []}
        // modifiers={selectedOptionItem?.modifiers ? [...selectedOptionItem.modifiers] : []}
        // totalPrice={totalPrice.toString()}
        setModifiers={(newModifiers) => {
        setSelectedOptionItem(prev => {
          // console.log("prev", prev?.modifiers);
          // console.log("newModifiers", newModifiers);
          if (!prev) return prev;
          return {
            ...prev,
            modifiers: [...newModifiers]
          };
        });

        setSelectedComboItem(prev => {
          if (!prev) return prev;
            const updatedCombo = { ...prev };
            updatedCombo.options.forEach(opt => {
              opt.items.forEach(item => {
                if (item.itemUniqueId === selectedOptionItem?.itemUniqueId) {
                  item.modifiers = [...newModifiers];
                }
              });
            });
            return updatedCombo;
          });
        }}
        totalPrice={totalPriceFormatted()}
        handleModifierChange={handleModifierChange}
        resetModifiers={() => resetSelectedOptionItem()}
      />
   
      {/* Cart Success Bottom Sheet */}
      <CartSuccessSheet
        show={showSuccessSheet}
        onClose={() => setShowSuccessSheet(false)}
      >
        <div className={cartsuccessstyles.illustration}>
          <Lottie animationData={itemAdded} loop={false} />
        </div>

        <div className={cartsuccessstyles.textContent}>
          <h2 className={cartsuccessstyles.title}>Item Added to Cart!</h2>
          <p className={cartsuccessstyles.description}>
            We&apos;ve added it to your cart. Ready to check out now, or want to keep shopping? We&apos;re here when you&apos;re ready.
          </p>
        </div>

        <div className={cartsuccessstyles.buttons}>
            <Link href={"/cart"+cartId} id="success-view-cart-link" className={cartsuccessstyles.viewCart}>
                <button className={cartsuccessstyles.viewCart} onClick={() => {
                  setShowSuccessSheet(false);
                  router.push('/cart' + cartId);
                }}>
                    View Cart
                </button>
            </Link>
            <Link href="/menu/all" id="success-continue-shopping-link" className={cartsuccessstyles.continueShopping}>
                <button className={cartsuccessstyles.continueShopping} onClick={() => {
                  setShowSuccessSheet(false);

                  if(isBottomSheet) {
                    (toggleBottomSheet as () => void)();
                  } else{
                    router.push('/menu/all');
                  }
                }}>
                    Continue Browsing
                </button>
            </Link>
        </div>
      </CartSuccessSheet>

    </div>
  </RemoveScroll>);
}
